import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	IonContent,
	IonLabel,
	IonPage,
	IonSegment,
	IonSegmentButton,
} from '@ionic/react';
import { SegmentContainer } from '../../components/PlacePage/Media/mediaStyles';
import PageHeader from '../../components/Global/PageHeader/PageHeader';
import { useLocation } from 'react-router-dom';
import ResultsHandler from '../../components/SearchResults/ResultsHandler';
import Footer from '../../components/Global/Footer/Footer';
import { ExplorerContext } from '../../application/config';
import {
	PlaceFactory,
	PlaceQuery,
} from '../../models/factories/PlaceFactory';
import {
	TrailFactory,
	TrailQuery,
} from '../../models/factories/TrailFactory';
import {
	StoryFactory,
	StoryQuery,
} from '../../models/factories/StoryFactory';

type SearchResultsProps = {};

const SearchResults: React.FC<SearchResultsProps> = () => {
	const [listKey, setListKey] = useState('places');
	const location = useLocation();
	const AppData = useContext(ExplorerContext);
	const search = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return params.get('q')?.trim().toLowerCase() ?? '';
	}, [location.search]);

	const placeCount = useMemo(() => {
		const query: PlaceQuery = {
			filter: {
				title: search,
			},
		};
		const results = PlaceFactory.filter(
			AppData.features?.all_places ?? PlaceFactory.getEmpty(),
			query,
		);
		return results.places.length;
	}, [AppData.features?.all_places, search]);

	const trailCount = useMemo(() => {
		const query: TrailQuery = {
			filter: {
				title: search,
			},
		};
		const results = TrailFactory.filter(
			AppData.features?.all_trails ?? TrailFactory.getEmpty(),
			query,
		);
		return results.trails.length;
	}, [AppData.features?.all_trails, search]);

	const storyCount = useMemo(() => {
		const query: StoryQuery = {
			filter: {
				title: search,
			},
		};
		const results = StoryFactory.filter(
			AppData.features?.all_stories ?? StoryFactory.getEmpty(),
			query,
		);
		return results.stories.length;
	}, [AppData.features?.all_stories, search]);

	useEffect(() => {
		const counts = {
			places: placeCount,
			trails: trailCount,
			stories: storyCount,
		};
		const firstWithResults =
			(['places', 'trails', 'stories'] as const).find(
				(segment) => counts[segment] > 0,
			) ?? 'places';
		setListKey(firstWithResults);
	}, [search, placeCount, trailCount, storyCount]);

	if (!search) {
		return (
			<IonPage>
				<IonContent>
					<PageHeader title="Search results" />
					<main style={{ padding: '2rem', textAlign: 'center' }}>
						<h3>No search term provided</h3>
						<p>Use the search bar on the home page to find places, trails, and stories.</p>
					</main>
					<Footer />
				</IonContent>
			</IonPage>
		);
	}

	return (
		<IonPage>
			<IonContent>
				<PageHeader title="Search results" />
				<h3 className="ph3">Showing results for: {search}</h3>

				<SegmentContainer>
					<IonSegment
						value={listKey}
						onIonChange={(e) => {
							if (e.detail.value === undefined) return;
							setListKey(e.detail.value);
						}}>
						<IonSegmentButton value="places">
							<IonLabel>Places</IonLabel>
						</IonSegmentButton>
						<IonSegmentButton value="trails">
							<IonLabel>Trails</IonLabel>
						</IonSegmentButton>
						<IonSegmentButton value="stories">
							<IonLabel>Stories</IonLabel>
						</IonSegmentButton>
					</IonSegment>
				</SegmentContainer>

				<ResultsHandler listKey={listKey} term={search} />
				<Footer />
			</IonContent>
		</IonPage>
	);
};

export default SearchResults;
